import React from "react";

/*
 * Line icons for the Home and About pages: one 24px grid, 2px rounded strokes, colour from
 * currentColor, so every icon follows the text or badge colour it sits in.
 * Decorative by default (aria-hidden); pass `title` when an icon carries meaning on its own.
 */
const Icon = ({ title, children, className = "", filled = false, ...rest }) => (
	<svg
		className={className}
		viewBox="0 0 24 24"
		fill={filled ? "currentColor" : "none"}
		stroke={filled ? "none" : "currentColor"}
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden={title ? undefined : "true"}
		role={title ? "img" : undefined}
		focusable="false"
		{...rest}
	>
		{title && <title>{title}</title>}
		{children}
	</svg>
);

export const IconArrow = (props) => (
	<Icon {...props}>
		<path d="M5 12h14" />
		<path d="m13 6 6 6-6 6" />
	</Icon>
);

export const IconChevron = (props) => (
	<Icon {...props}>
		<path d="m9 6 6 6-6 6" />
	</Icon>
);

export const IconPlay = (props) => (
	<Icon filled {...props}>
		<path d="M7 4.8v14.4a1 1 0 0 0 1.52.85l11.5-7.2a1 1 0 0 0 0-1.7L8.52 3.95A1 1 0 0 0 7 4.8Z" />
	</Icon>
);

export const IconClose = (props) => (
	<Icon {...props}>
		<path d="M6 6l12 12M18 6 6 18" />
	</Icon>
);

export const IconUsers = (props) => (
	<Icon {...props}>
		<circle cx="9" cy="8" r="3.5" />
		<path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
		<path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" />
		<path d="M18 14.2a6.5 6.5 0 0 1 3.5 5.8" />
	</Icon>
);

/** Teacher-Controlled Content */
export const IconCap = (props) => (
	<Icon {...props}>
		<path d="M2 9.5 12 5l10 4.5-10 4.5L2 9.5Z" />
		<path d="M6 11.3V16c0 1.5 2.7 3 6 3s6-1.5 6-3v-4.7" />
		<path d="M22 9.5V15" />
	</Icon>
);

/** Interactive Student Engagement / Engage */
export const IconGamepad = (props) => (
	<Icon {...props}>
		<path d="M7 7h10a5 5 0 0 1 4.9 6l-.7 3.4a2.5 2.5 0 0 1-4.3 1.2L14.6 15H9.4l-2.3 2.6a2.5 2.5 0 0 1-4.3-1.2L2.1 13A5 5 0 0 1 7 7Z" />
		<path d="M7.5 10v3M6 11.5h3" />
		<circle cx="16" cy="10.5" r="0.6" fill="currentColor" />
		<circle cx="17.5" cy="12.5" r="0.6" fill="currentColor" />
	</Icon>
);

/** Performance Insights / Track */
export const IconChart = (props) => (
	<Icon {...props}>
		<path d="M4 20V13" />
		<path d="M10 20V8" />
		<path d="M16 20v-5" />
		<path d="M21 20V4" />
		<path d="M2 20h20" />
	</Icon>
);

/** Create */
export const IconCreate = (props) => (
	<Icon {...props}>
		<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h5" />
		<path d="M14 3v4a1 1 0 0 0 1 1h4" />
		<path d="M19 8v3" />
		<path d="M14 3l5 5" />
		<path d="M9 12h5M9 16h3" />
		<circle cx="18" cy="18" r="4" />
		<path d="M18 16.3v3.4M16.3 18h3.4" />
	</Icon>
);

/** Reuse */
export const IconReuse = (props) => (
	<Icon {...props}>
		<path d="M20 11a8 8 0 0 0-14.3-4.9L4 8" />
		<path d="M4 3v5h5" />
		<path d="M4 13a8 8 0 0 0 14.3 4.9L20 16" />
		<path d="M20 21v-5h-5" />
	</Icon>
);

export const IconTrophy = (props) => (
	<Icon {...props}>
		<path d="M8 4h8v5a4 4 0 0 1-8 0V4Z" />
		<path d="M8 6H5a1 1 0 0 0-1 1 4 4 0 0 0 4 4" />
		<path d="M16 6h3a1 1 0 0 1 1 1 4 4 0 0 1-4 4" />
		<path d="M12 13v4" />
		<path d="M8 21h8l-1-4H9l-1 4Z" />
	</Icon>
);

export const IconTarget = (props) => (
	<Icon {...props}>
		<circle cx="12" cy="12" r="9" />
		<circle cx="12" cy="12" r="5" />
		<circle cx="12" cy="12" r="1.2" fill="currentColor" />
		<path d="m12 12 7-7M17 3v2.5a.5.5 0 0 0 .5.5H21" />
	</Icon>
);

/** Plans */
export const IconPerson = (props) => (
	<Icon {...props}>
		<circle cx="12" cy="7.5" r="3.5" />
		<path d="M5 21v-2a7 7 0 0 1 14 0v2" />
	</Icon>
);

export const IconBuilding = (props) => (
	<Icon {...props}>
		<path d="M3 21h18" />
		<path d="M4 9.5 12 4l8 5.5" />
		<path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" />
		<path d="M4 18h16" />
	</Icon>
);

export const IconQuote = (props) => (
	<Icon filled {...props}>
		<path d="M9.6 6C6.5 7.3 4.5 10.2 4.5 13.6V18h5.6v-5.6H7.4c.1-2 1.3-3.8 3.1-4.7L9.6 6Zm9 0c-3.1 1.3-5.1 4.2-5.1 7.6V18h5.6v-5.6h-2.7c.1-2 1.3-3.8 3.1-4.7L18.6 6Z" />
	</Icon>
);

export const IconFlag = (props) => (
	<Icon {...props}>
		<path d="M5 21V4" />
		<path d="M5 4h11l-2 4 2 4H5" />
	</Icon>
);

export const IconPhone = (props) => (
	<Icon {...props}>
		<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2Z" />
	</Icon>
);

export const IconMail = (props) => (
	<Icon {...props}>
		<rect x="3" y="5" width="18" height="14" rx="2" />
		<path d="m3 7 9 6 9-6" />
	</Icon>
);

export const IconShield = (props) => (
	<Icon {...props}>
		<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
		<path d="m9 12 2 2 4-4" />
	</Icon>
);

/** Icon for each pillar, step and plan key in site.data.js (one icon per idea, on every page). */
export const KEY_ICONS = {
	control: IconCap,
	engagement: IconGamepad,
	insights: IconChart,
	create: IconCreate,
	engage: IconGamepad,
	track: IconChart,
	reuse: IconReuse,
	basic: IconPerson,
	premium: IconUsers,
	professional: IconBuilding,
};

/** Round tinted badge with the icon for `iconKey`, in the colours of `tone` (blue, green, purple, orange). */
export const ToneIcon = ({ iconKey, tone, solid = false, className = "" }) => {
	const Glyph = KEY_ICONS[iconKey];
	return (
		<span className={`sm_icon sm_icon--${tone} ${solid ? "sm_icon--solid" : ""} ${className}`.trim()} aria-hidden="true">
			{Glyph && <Glyph />}
		</span>
	);
};
